import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useProjectStore } from '../../store/projectStore';
import SafeIcon from '../../common/SafeIcon';
import CreateProject from '../../components/Projects/CreateProject';
import ProjectDetail from '../../components/Projects/ProjectDetail';
import ShareModal from '../../components/Projects/ShareModal';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiSearch, FiFilter, FiMoreVertical, FiEye, FiEdit, FiTrash2, FiShare2, FiLoader } = FiIcons;

const Projects = () => {
  const navigate = useNavigate();
  const { projects, fetchProjects, isLoading, error } = useProjectStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [shareModal, setShareModal] = useState({ isOpen: false, project: null });

  // Fetch projects when component mounts
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         project.company?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleShare = (project) => {
    setShareModal({ isOpen: true, project });
  };

  const closeShareModal = () => {
    setShareModal({ isOpen: false, project: null });
  };

  return (
    <>
      <Routes>
        <Route path="/" element={
          <div className="p-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Proyectos</h1>
                  <p className="text-gray-600">Gestiona tus entrevistas y procesos de selección</p>
                </div>
                <Link
                  to="new"
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                >
                  <SafeIcon icon={FiPlus} className="w-4 h-4" />
                  <span>Nuevo Proyecto</span>
                </Link>
              </div>

              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <SafeIcon
                    icon={FiSearch}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"
                  />
                  <input
                    type="text"
                    placeholder="Buscar por título o empresa..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">Todos los estados</option>
                  <option value="active">Activos</option>
                  <option value="completed">Completados</option>
                  <option value="archived">Archivados</option>
                </select>
              </div>
            </motion.div>

            {/* Loading state */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <SafeIcon icon={FiLoader} className="w-8 h-8 text-primary animate-spin" />
                <span className="ml-2 text-gray-600">Cargando proyectos...</span>
              </div>
            )}

            {/* Error state */}
            {error && (
              <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
                <p>Error al cargar los proyectos: {error}</p>
                <button 
                  onClick={fetchProjects}
                  className="mt-2 text-red-700 underline"
                >
                  Intentar de nuevo
                </button>
              </div>
            )}

            {/* Projects Grid */}
            {!isLoading && filteredProjects.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProjects.map((project) => (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow"
                  >
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">{project.title}</h3>
                        <div className="relative group">
                          <button className="p-1 text-gray-400 hover:text-gray-600">
                            <SafeIcon icon={FiMoreVertical} className="w-5 h-5" />
                          </button>
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                            <div className="py-1">
                              <button
                                onClick={() => navigate(`${project.id}`)}
                                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                              >
                                <SafeIcon icon={FiEye} className="w-4 h-4" />
                                <span>Ver detalles</span>
                              </button>
                              <button
                                onClick={() => handleShare(project)}
                                className="flex items-center space-x-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 w-full text-left"
                              >
                                <SafeIcon icon={FiShare2} className="w-4 h-4" />
                                <span>Compartir</span>
                              </button>
                              <button className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left">
                                <SafeIcon icon={FiEdit} className="w-4 h-4" />
                                <span>Editar</span>
                              </button>
                              <button className="flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left">
                                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                                <span>Eliminar</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-600 mb-4">{project.company}</p>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">
                          ID: {project.vixeaId}
                        </span>
                        <span
                          className={`px-2 py-1 rounded-full ${
                            project.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : project.status === 'completed'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                        </span>
                      </div>
                    </div>
                    <div className="px-6 py-4 bg-gray-50 rounded-b-xl">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                          {new Date(project.createdAt).toLocaleDateString()}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleShare(project)}
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                          >
                            Compartir
                          </button>
                          <span className="text-gray-300">•</span>
                          <button
                            onClick={() => navigate(`${project.id}`)}
                            className="text-primary hover:text-blue-700 text-sm font-medium"
                          >
                            Ver detalles →
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : !isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiFilter} className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {searchTerm || statusFilter !== 'all'
                    ? 'No se encontraron proyectos'
                    : 'No hay proyectos aún'}
                </h3>
                <p className="text-gray-600">
                  {searchTerm || statusFilter !== 'all'
                    ? 'Intenta ajustar tus filtros de búsqueda'
                    : 'Crea tu primer proyecto para comenzar'}
                </p>
                {!searchTerm && statusFilter === 'all' && (
                  <Link
                    to="new"
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors mt-4"
                  >
                    <SafeIcon icon={FiPlus} className="w-4 h-4" />
                    <span>Crear Proyecto</span>
                  </Link>
                )}
              </motion.div>
            )}
          </div>
        } />
        <Route path="new" element={<CreateProject />} />
        <Route path=":id" element={<ProjectDetail />} />
      </Routes>

      {/* Share Modal */}
      <ShareModal
        project={shareModal.project}
        isOpen={shareModal.isOpen}
        onClose={closeShareModal}
      />
    </>
  );
};

export default Projects;